import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '@/pages/gerenciar/direcao/mensagemBanco';
import Cartao from '@/components/ui/Cartao';
import Chip from '@/components/ui/Chip';
import EstadoVazio from '@/components/ui/EstadoVazio';
import {
  dataHoraBrasilia,
  formatarDinheiro,
  horaBrasilia,
  inicioDoDiaBrasiliaISO,
  normalizarVendas,
  type VendasDados,
} from './utils';

interface Props {
  eventoId: number;
}

type Periodo = 'hoje' | 'evento';

function Tile({
  titulo,
  valor,
  destaque,
}: {
  titulo: string;
  valor: string;
  destaque?: boolean;
}) {
  return (
    <div className="rounded-2xl bg-white p-3 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <p className="text-[12px] font-medium text-foreground-500">{titulo}</p>
      <p className={`mt-1 text-[20px] font-extrabold leading-tight ${destaque ? 'text-accent-600' : 'text-foreground-950'}`}>
        {valor}
      </p>
    </div>
  );
}

function BarraMeio({
  rotulo,
  centavos,
  vendas,
  max,
  cor,
}: {
  rotulo: string;
  centavos: number;
  vendas: number;
  max: number;
  cor: string;
}) {
  const pct = max > 0 ? Math.max(2, Math.round((centavos / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-medium text-foreground-800">{rotulo}</span>
        <span className="text-[13px] text-foreground-500">
          {formatarDinheiro(centavos)} · {vendas} {vendas === 1 ? 'venda' : 'vendas'}
        </span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-background-200">
        <div className={`h-full rounded-full ${cor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function VisaoVendas({ eventoId }: Props) {
  const [periodo, setPeriodo] = useState<Periodo>('hoje');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dados, setDados] = useState<VendasDados | null>(null);
  const [atualizando, setAtualizando] = useState(false);
  const [reload, setReload] = useState(0);
  const [verDesfeitas, setVerDesfeitas] = useState(false);

  const recarregar = useCallback(() => setReload((r) => r + 1), []);

  useEffect(() => {
    let ativo = true;

    async function carregar(primeira: boolean) {
      if (primeira) setCarregando(true);
      else setAtualizando(true);
      setErro(null);
      try {
        const { data, error } = await supabase.rpc('equipe_vendas', {
          p_evento: eventoId,
          p_desde: periodo === 'hoje' ? inicioDoDiaBrasiliaISO() : null,
          p_ate: null,
        });
        if (error) throw error;
        if (!ativo) return;
        const normalizado = normalizarVendas(data) ?? null;
        setDados(normalizado);
      } catch (e) {
        if (ativo) setErro(mensagemBanco(e));
      } finally {
        if (ativo) {
          setCarregando(false);
          setAtualizando(false);
        }
      }
    }

    void carregar(true);
    return () => {
      ativo = false;
    };
  }, [eventoId, periodo, reload]);

  // Recarrega sozinho a cada 60s enquanto a tela estiver aberta.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        setAtualizando(true);
        setReload((r) => r + 1);
      }
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const resumo = dados?.resumo;
  const totalVendas = (resumo?.caixa_vendas ?? 0) + (resumo?.online_vendas ?? 0);
  const semVendas =
    !!resumo && resumo.caixa_vendas === 0 && resumo.online_vendas === 0 && resumo.fichas_entregues_na_barraca === 0;

  const maxMeio = Math.max(
    dados?.por_meio.dinheiro?.total_centavos ?? 0,
    dados?.por_meio.pix?.total_centavos ?? 0,
    dados?.por_meio.cartao?.total_centavos ?? 0,
    dados?.por_meio.outro?.total_centavos ?? 0,
  );
  const maxHora = Math.max(1, ...(dados?.por_hora ?? []).map((h) => h.vendas));

  return (
    <div className="space-y-4">
      {/* Filtro de período */}
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex rounded-full bg-white p-1 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
          {(['hoje', 'evento'] as Periodo[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriodo(p)}
              className={`min-h-[40px] cursor-pointer whitespace-nowrap rounded-full px-4 text-[14px] font-semibold transition-colors ${
                periodo === p ? 'bg-primary-500 text-white' : 'text-foreground-700 hover:bg-primary-50'
              }`}
            >
              {p === 'hoje' ? 'Hoje' : 'Evento todo'}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            setAtualizando(true);
            recarregar();
          }}
          className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-3 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
        >
          <span className={`flex h-4 w-4 items-center justify-center ${atualizando ? 'animate-spin' : ''}`}>
            <i className="ri-refresh-line" />
          </span>
          Atualizar
        </button>
      </div>

      {carregando ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-20 esqueleto rounded-2xl bg-secondary-100" />
            ))}
          </div>
          <div className="h-40 esqueleto rounded-3xl bg-secondary-100" />
        </div>
      ) : erro ? (
        <Cartao className="p-10 text-center">
          <p className="text-[15px] text-foreground-800">{erro}</p>
          <button
            type="button"
            onClick={recarregar}
            className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Tentar de novo
          </button>
        </Cartao>
      ) : !dados || !resumo ? (
        <EstadoVazio icone="ri-line-chart-line" titulo="Sem dados de vendas" descricao="Assim que houver vendas, elas aparecem aqui." />
      ) : semVendas ? (
        <EstadoVazio
          icone="ri-line-chart-line"
          titulo="Nenhuma venda ainda"
          descricao={periodo === 'hoje' ? 'Nada vendido hoje até agora.' : 'Nada vendido neste evento ainda.'}
        />
      ) : (
        <>
          {/* Tiles */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile titulo="Vendido no caixa" valor={formatarDinheiro(resumo.caixa_total_centavos)} />
            <Tile titulo="Vendas" valor={String(totalVendas)} />
            <Tile titulo="Fichas entregues" valor={String(resumo.fichas_entregues_na_barraca)} />
            {resumo.online_total_centavos > 0 && (
              <Tile titulo="Online" valor={formatarDinheiro(resumo.online_total_centavos)} destaque />
            )}
          </div>

          {/* Por meio */}
          <Cartao className="p-4">
            <h2 className="text-[16px] font-bold text-foreground-950">Por meio de pagamento</h2>
            <div className="mt-3 space-y-3">
              <BarraMeio rotulo="Dinheiro" centavos={dados.por_meio.dinheiro?.total_centavos ?? 0} vendas={dados.por_meio.dinheiro?.vendas ?? 0} max={maxMeio} cor="bg-success-500" />
              <BarraMeio rotulo="Pix" centavos={dados.por_meio.pix?.total_centavos ?? 0} vendas={dados.por_meio.pix?.vendas ?? 0} max={maxMeio} cor="bg-primary-500" />
              <BarraMeio rotulo="Cartão" centavos={dados.por_meio.cartao?.total_centavos ?? 0} vendas={dados.por_meio.cartao?.vendas ?? 0} max={maxMeio} cor="bg-accent-500" />
              {(dados.por_meio.outro?.total_centavos ?? 0) > 0 && (
                <BarraMeio rotulo="Outro" centavos={dados.por_meio.outro?.total_centavos ?? 0} vendas={dados.por_meio.outro?.vendas ?? 0} max={maxMeio} cor="bg-secondary-400" />
              )}
            </div>
          </Cartao>

          {/* Por hora */}
          {dados.por_hora.length > 0 && (
            <Cartao className="p-4">
              <h2 className="text-[16px] font-bold text-foreground-950">Por hora</h2>
              <div className="mt-4 flex items-end gap-1.5 overflow-x-auto pb-1">
                {dados.por_hora.map((h) => (
                  <div key={h.hora} className="flex min-w-[40px] flex-1 flex-col items-center gap-1" title={`${h.hora} · ${h.vendas} vendas · ${formatarDinheiro(h.total_centavos)}`}>
                    <span className="text-[11px] font-semibold text-foreground-600">{h.vendas}</span>
                    <div className="flex h-24 w-full items-end">
                      <div
                        className="w-full rounded-t-md bg-primary-500/80"
                        style={{ height: `${Math.max(4, Math.round((h.vendas / maxHora) * 100))}%` }}
                      />
                    </div>
                    <span className="whitespace-nowrap text-[10px] text-foreground-500">{h.hora}</span>
                  </div>
                ))}
              </div>
            </Cartao>
          )}

          {/* Caixa por pessoa */}
          {dados.caixa_por_pessoa.length > 0 && (
            <Cartao className="p-4">
              <h2 className="text-[16px] font-bold text-foreground-950">Caixa por pessoa</h2>
              <ul className="mt-3 space-y-3">
                {dados.caixa_por_pessoa.map((p, i) => (
                  <li key={`${p.nome}-${i}`} className="rounded-2xl bg-background-100 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[15px] font-semibold text-foreground-950">{p.nome}</p>
                        <p className="mt-0.5 text-[13px] text-foreground-500">
                          {p.caixas} {p.caixas === 1 ? 'caixa' : 'caixas'} · {p.vendas}{' '}
                          {p.vendas === 1 ? 'venda' : 'vendas'}
                          {p.ultima_em ? ` · última ${horaBrasilia(p.ultima_em)}` : ''}
                        </p>
                      </div>
                      <p className="shrink-0 text-[18px] font-extrabold text-foreground-950">
                        {formatarDinheiro(p.total_centavos)}
                      </p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-success-100 px-2.5 py-1 text-[12px] font-semibold text-success-800">
                        💵 {formatarDinheiro(p.dinheiro_centavos)}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[12px] font-semibold text-primary-700">
                        Pix {formatarDinheiro(p.pix_centavos)}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-[12px] font-semibold text-accent-900">
                        💳 {formatarDinheiro(p.cartao_centavos)}
                      </span>
                      {p.desfeitas > 0 && <Chip tipo="warning">{p.desfeitas} desfeitas</Chip>}
                    </div>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          {/* Barracas */}
          {dados.barracas.length > 0 && (
            <Cartao className="p-4">
              <h2 className="text-[16px] font-bold text-foreground-950">Barracas</h2>
              <ul className="mt-3 space-y-3">
                {dados.barracas.map((b, i) => (
                  <li key={`${b.barraca}-${i}`} className="rounded-2xl bg-background-100 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="truncate text-[15px] font-semibold text-foreground-950">{b.barraca}</p>
                      <span className="shrink-0 text-[13px] font-semibold text-accent-700">
                        {b.fichas_entregues} {b.fichas_entregues === 1 ? 'ficha' : 'fichas'}
                      </span>
                    </div>
                    {b.por_pessoa.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {b.por_pessoa.map((pp, j) => (
                          <li key={`${pp.nome}-${j}`} className="flex items-center justify-between gap-2">
                            <span className="truncate text-[13px] text-foreground-800">{pp.nome}</span>
                            <span className="shrink-0 text-[13px] text-foreground-500">
                              {pp.fichas}
                              {pp.ultima_em ? ` · ${horaBrasilia(pp.ultima_em)}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          {/* Desfeitas */}
          {dados.desfeitas.length > 0 && (
            <Cartao className="p-4">
              <button
                type="button"
                onClick={() => setVerDesfeitas((v) => !v)}
                className="flex w-full cursor-pointer items-center justify-between gap-2"
              >
                <span className="text-[16px] font-bold text-foreground-950">
                  Ver desfeitas ({dados.desfeitas.length})
                </span>
                <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                  <i className={verDesfeitas ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                </span>
              </button>
              {verDesfeitas && (
                <ul className="mt-3 space-y-2">
                  {dados.desfeitas.map((d, i) => (
                    <li key={`${d.numero}-${i}`} className="flex items-start justify-between gap-3 rounded-xl bg-background-100 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-[14px] font-medium text-foreground-950">#{d.numero}</p>
                        <p className="text-[12px] text-foreground-500">
                          {d.vendeu || 'sem nome'}
                          {d.em ? ` · ${dataHoraBrasilia(d.em)}` : ''}
                          {d.motivo ? ` · ${d.motivo}` : ''}
                        </p>
                      </div>
                      <span className="shrink-0 text-[14px] font-semibold text-danger-600">
                        {formatarDinheiro(d.total_centavos)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Cartao>
          )}
        </>
      )}
    </div>
  );
}